/* Weekly cycle, step 3 - simulations.
 *
 * Two tabs, because they are two different things:
 *   Simulations    every what-if run, automatic or manual, newest first -
 *                  one list, one way to open them
 *   Current plan   the plan the reports read, as the workbook grid, where a
 *                  planner can still edit a cell or revert the last apply
 */

import { useEffect, useState } from 'react'
import { api, type SimulationSummary, type StreamSummary } from '../api/client'
import { describeOptions, when } from '../components/simulation'
import { Applied, IconButton, ModeChip } from '../components/icons'
import { MultiSelect } from '../components/MultiSelect'
import { Simulation } from './Simulation'

interface Props {
  streams: StreamSummary[]
  canEdit: boolean
  /* Bumped by the parent after a simulation is created, edited or deleted. */
  refreshKey: number
  onOpen: (id: number) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onNew: (preselected: string[], lock?: boolean) => void
  onPlanChanged: () => void
}

export function Simulations({ streams, canEdit, refreshKey, onOpen, onEdit, onDelete, onNew, onPlanChanged }: Props) {
  const [tab, setTab] = useState<'list' | 'plan'>('list')
  /* Families shown; empty = all. */
  const [families, setFamilies] = useState<string[]>([])
  const [rows, setRows] = useState<SimulationSummary[] | null>(null)
  const [chosen, setChosen] = useState<Record<string, number | null>>({})
  const [error, setError] = useState<string | null>(null)
  const [current, setCurrent] = useState(streams[0]?.id ?? '')

  useEffect(() => {
    api
      .simulations()
      .then((d) => {
        setRows(d.simulations)
        setChosen(d.chosen)
      })
      .catch((e: Error) => setError(e.message))
  }, [refreshKey])

  const shown = rows && families.length ? rows.filter((r) => families.includes(r.value_stream)) : rows

  useEffect(() => {
    if (!current && streams[0]) setCurrent(streams[0].id)
  }, [streams, current])

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card page-head">
        <div className="page-title">
          <h1>Simulations</h1>
        </div>
        <div className="tabs" style={{ border: 'none' }}>
          <button className={tab === 'list' ? 'on' : ''} onClick={() => setTab('list')}>
            Simulations
          </button>
          <button className={tab === 'plan' ? 'on' : ''} onClick={() => setTab('plan')}>
            Current plan
          </button>
        </div>
        <div className="page-actions">
          {tab === 'list' && (
            <MultiSelect
              label="Value stream"
              options={streams.map((s) => ({ id: s.id, label: s.name }))}
              value={families}
              onChange={setFamilies}
            />
          )}
          {canEdit && (
            <button
              className="primary"
              onClick={() => (tab === 'plan' && current ? onNew([current], true) : onNew(families))}
            >
              + New simulation
            </button>
          )}
        </div>
      </div>

      {tab === 'list' ? (
        <>
          {error && <div className="card" style={{ padding: 16 }}>{error}</div>}
          <SimulationTable rows={shown} chosen={chosen} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} canEdit={canEdit} />
        </>
      ) : (
        <>
          <div className="card" style={{ padding: 8, display: 'flex', gap: 6, overflowX: 'auto' }}>
            {streams.map((s) => (
              <button
                key={s.id}
                onClick={() => setCurrent(s.id)}
                style={{
                  whiteSpace: 'nowrap',
                  borderColor: current === s.id ? 'var(--accent)' : 'var(--border)',
                  background: current === s.id ? 'var(--surface-3)' : 'var(--surface-1)',
                  fontWeight: current === s.id ? 600 : 400,
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
          {current && (
            <Simulation valueStreamId={current} scenario="Working" canEdit={canEdit} onPlanApplied={onPlanChanged} />
          )}
        </>
      )}
    </div>
  )
}

export function SimulationTable({
  rows,
  chosen,
  onOpen,
  onEdit,
  onDelete,
  canEdit,
  compact,
}: {
  rows: SimulationSummary[] | null
  /* family -> the simulation it is shown with: that one is applied. */
  chosen: Record<string, number | null>
  onOpen: (id: number) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  canEdit: boolean
  compact?: boolean
}) {
  if (!rows) return <div className="card" style={{ padding: 16 }}>Loading…</div>
  if (!rows.length)
    return (
      <div className={compact ? 'small muted' : 'card small muted'} style={{ padding: compact ? 4 : 16 }}>
        No simulation yet.
      </div>
    )
  return (
    <div className={compact ? '' : 'card'} style={{ padding: compact ? 0 : 0, overflow: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            {['Simulation', 'Family', 'Mode', 'Created', 'In band', 'Short', 'Excess', 'Status', ''].map((h) => (
              <th key={h || 'open'} className={['Simulation', 'Family', 'Mode', 'Created', 'Status'].includes(h) ? 'left' : ''}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(r.id)}>
              <td className="left">
                <strong>{r.name}</strong>
                <div className="small muted">{describeOptions(r)}</div>
              </td>
              <td className="left">{r.value_stream}</td>
              <td className="left">
                <ModeChip mode={r.mode} />
              </td>
              <td className="left small" title={`by ${r.created_by}`}>
                {when(r.created_at)}
              </td>
              <Move before={r.before.weeks_in_band} after={r.after.weeks_in_band} higherIsBetter />
              <Move before={r.before.weeks_below_min} after={r.after.weeks_below_min} />
              <Move before={r.before.weeks_above_max} after={r.after.weeks_above_max} />
              <td className="left">
                <Applied on={chosen[r.value_stream] === r.id} />
              </td>
              <td>
                <div className="row-actions">
                  <IconButton icon="open" title="Open" onClick={() => onOpen(r.id)} />
                  {canEdit && (
                    <>
                      <IconButton icon="edit" title="Edit" onClick={() => onEdit(r.id)} />
                      <IconButton icon="trash" title="Delete" danger onClick={() => onDelete(r.id)} />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Move({ before, after, higherIsBetter }: { before: number; after: number; higherIsBetter?: boolean }) {
  const moved = after - before
  const better = higherIsBetter ? moved > 0 : moved < 0
  return (
    <td className="num">
      <span className="muted">{before}</span> →{' '}
      <strong style={{ color: moved === 0 ? undefined : better ? 'var(--status-good)' : 'var(--status-critical)' }}>
        {after}
      </strong>
    </td>
  )
}
